/**
 * useVerifyTrainingCompletion — Batch 18-E9
 *
 * Thin client wrapper around the verify-training-completion Edge
 * Function. ALL authority checks happen server-side (see
 * supabase/functions/verify-training-completion/index.ts §Check 1-9).
 * This hook performs NO direct DB writes — it cannot, by design,
 * bypass the Edge Function's role / completion-state / self-verify
 * guards.
 *
 * No UI in 18-E9. This hook exists so that a follow-on UI batch
 * (18-E11) has a stable surface to call.
 *
 * The hook does NOT:
 *   - write training_consumption_sessions directly
 *   - write training_completion_verifications directly
 *   - insert training_effectiveness_events
 *   - trigger signal-processing functions
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface VerifyTrainingCompletionInput {
  sessionId: string;
  note?: string;
}

export interface VerifyTrainingCompletionResult {
  verificationId: string;
  sessionId: string;
  manager_verified_at: string;
  verified_by_role:
    | 'MANAGER'
    | 'OWNER'
    | 'ORG_ADMIN'
    | 'INTERNAL_ADMIN';
}

export function useVerifyTrainingCompletion() {
  const queryClient = useQueryClient();

  return useMutation<
    VerifyTrainingCompletionResult,
    Error,
    VerifyTrainingCompletionInput
  >({
    mutationFn: async ({ sessionId, note }) => {
      const { data, error } = await supabase.functions.invoke(
        'verify-training-completion',
        {
          body: { sessionId, note },
        },
      );
      if (error) throw error;
      return data as VerifyTrainingCompletionResult;
    },
    onSuccess: (_result, variables) => {
      // Invalidate any in-flight session queries so the UI picks up
      // the new manager_verified_at / completion_confidence='verified'
      // pointer-column state the Edge Function just wrote.
      queryClient.invalidateQueries({
        queryKey: ['consumption-sessions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['training-completion-verifications', variables.sessionId],
      });
    },
  });
}
